import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { setCookie, getCookie } from '../utils/cookieUtils';

const AnalyticsConsent = ({ onConsentChange }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showDeclineOption, setShowDeclineOption] = useState(false);
    const [consent, setConsent] = useState(() => {
        const savedConsent = getCookie('analyticsConsent');
        return savedConsent ? JSON.parse(savedConsent) : null;
    });
    const { isDarkMode } = useTheme();

    useEffect(() => {
        // Only show popup if consent hasn't been set yet
        if (consent === null) {
            setShowPopup(true);
        }
    }, []);

    const handleAccept = () => {
        setConsent(true);
        setCookie('analyticsConsent', 'true');
        setShowPopup(false);
        setShowSettings(false);
        onConsentChange(true);
        
        // Initialize GTM
        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'consent_given',
                consent_type: 'analytics'
            });
        }
    };

    const handleDecline = () => {
        setConsent(false);
        setCookie('analyticsConsent', 'false');
        setShowPopup(false);
        setShowSettings(false);
        onConsentChange(false);
        
        // Track consent decline
        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'consent_declined',
                consent_type: 'analytics'
            });
        }
    };

    if (!showPopup) return null;

    return (
        <>
            {/* Original bottom banner */}
            {!showSettings && (
                <div className="fixed bottom-0 left-0 right-0 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 z-50">
                    <div className="max-w-4xl mx-auto">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="text-gray-700 dark:text-gray-300 flex-1">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Cookie Consent</h3>
                                <p className="text-sm">
                                    We use Google Analytics to collect anonymous usage data that helps us understand how our service is being used. 
                                    This information enables us to continuously improve the calculator and provide a better experience for all users.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 w-full sm:w-48">
                                <button
                                    onClick={handleAccept}
                                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                                        isDarkMode
                                            ? 'bg-cyan-500 hover:bg-cyan-600 text-white'
                                            : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                                    }`}
                                >
                                    Accept
                                </button>
                                <button
                                    onClick={() => setShowSettings(true)}
                                    className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-300 transition-colors text-sm"
                                >
                                    Manage Settings
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detailed settings popup */}
            {showSettings && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`p-6 rounded-xl shadow-lg max-w-md mx-4 ${
                        isDarkMode 
                            ? 'bg-gray-800 text-white border border-gray-700' 
                            : 'bg-white text-gray-900 border border-gray-200'
                    }`}>
                        <h3 className="text-xl font-bold mb-2">Help Us Improve SaveMatic</h3>
                        <p className="mb-4 text-gray-600 dark:text-gray-300">
                            We use analytics to understand how you use SaveMatic, which helps us:
                        </p>
                        <ul className="list-disc list-inside mb-4 text-gray-600 dark:text-gray-300 space-y-1">
                            <li>Identify and fix issues quickly</li>
                            <li>Add features you'll love</li>
                            <li>Make the calculator more user-friendly</li>
                            <li>Ensure everything works perfectly</li>
                        </ul>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Your data is anonymous and helps us create a better experience for everyone.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={handleAccept}
                                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                                    isDarkMode
                                        ? 'bg-cyan-500 hover:bg-cyan-600 text-white'
                                        : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                                }`}
                            >
                                Accept & Continue
                            </button>
                            
                            {!showDeclineOption ? (
                                <button
                                    onClick={() => setShowDeclineOption(true)}
                                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                                        isDarkMode
                                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                    }`}
                                >
                                    Show More Options
                                </button>
                            ) : (
                                <div className="space-y-3">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
                                        Are you sure you want to decline? This will limit our ability to improve the service.
                                    </div>
                                    <button
                                        onClick={handleDecline}
                                        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                                            isDarkMode
                                                ? 'bg-cyan-500 hover:bg-cyan-600 text-white'
                                                : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                                        }`}
                                    >
                                        Yes
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AnalyticsConsent; 